import React, { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Button, Form, Message } from 'semantic-ui-react'
import { FormDropdownField, FormTextFormField } from 'elements'
import { createDeal, getAccounts, getContacts, updateDeal } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { editableStages } from 'utility/crm/deal-board'
import { toDateInput } from 'utility/crm/dates'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmQuery, useCrmToast } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'

const FORM_ID = 'crm-deal-form'

const schema = yup.object().shape({
    Name: yup.string().trim().required('Deal name is required'),
    AccountId: yup.mixed().test('account', 'Choose an account', v => v !== '' && v !== undefined && v !== null),
    Amount: yup
        .number()
        .transform((value, original) => (original === '' || original === null ? 0 : value))
        .typeError('Enter a number')
        .min(0, 'Amount cannot be negative'),
})

const FieldError = ({ message }) =>
    message ? (
        <p role="alert" style={{ color: '#dc2626', margin: '2px 0 8px', fontSize: '0.8rem' }}>
            {message}
        </p>
    ) : null

const Body = ({ deal, defaults, onSaved, onBusyChange }) => {
    const isEdit = Boolean(deal)
    const { lookups } = useCrmLookups()
    const { successToast } = useCrmToast()
    const accounts = useCrmQuery(() => getAccounts({ sort: 'name' }), [], { scopes: ['accounts'] })
    const [serverError, setServerError] = useState(null)

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            Name: deal?.Name || '',
            AccountId: deal?.AccountId ?? defaults.AccountId ?? '',
            ContactId: deal?.ContactId ?? '',
            Amount: deal?.Amount ?? '',
            Stage: deal?.Stage && deal.Stage !== 'Won' ? deal.Stage : defaults.Stage || 'New Lead',
            Pipeline: deal?.Pipeline || defaults.Pipeline || 'New business',
            OwnerUserId: deal?.OwnerUserId ?? '',
            CloseDate: deal?.CloseDate ? String(deal.CloseDate).slice(0, 10) : toDateInput(new Date(Date.now() + 30 * 86400000)),
        },
    })
    const accountId = watch('AccountId')

    // A deal's contact must belong to the deal's account, so only that account's contacts are offered.
    const contacts = useCrmQuery(() => getContacts({ AccountId: Number(accountId), PageSize: 200 }), [accountId], {
        enabled: accountId !== '' && accountId !== undefined,
    })

    const accountOptions = (accounts.data || []).map(a => ({ key: a.AccountId, value: a.AccountId, text: a.Name }))
    const contactOptions = useMemo(
        () => [{ key: 'none', value: '', text: 'No contact' }, ...(contacts.data?.Items || []).map(c => ({ key: c.ContactId, value: c.ContactId, text: c.Name }))],
        [contacts.data],
    )
    const stageOptions = editableStages(lookups?.Stages).map(n => ({ key: n, value: n, text: n }))
    const pipelineOptions = (lookups?.Pipelines || []).map(n => ({ key: n, value: n, text: n }))
    const ownerOptions = [{ key: 'none', value: '', text: 'Unassigned' }, ...(lookups?.Users || []).map(u => ({ key: u.UserId, value: u.UserId, text: u.FullName }))]

    const submit = async values => {
        setServerError(null)
        onBusyChange(true)
        /** @type {import('utility/crm/types').CrmDealInput} */
        const body = {
            AccountId: Number(values.AccountId),
            ContactId: values.ContactId === '' ? undefined : Number(values.ContactId),
            Name: values.Name.trim(),
            Amount: Number(values.Amount) || 0,
            Stage: values.Stage,
            Pipeline: values.Pipeline,
            OwnerUserId: values.OwnerUserId === '' ? undefined : Number(values.OwnerUserId),
            CloseDate: values.CloseDate || undefined,
        }
        try {
            const saved = isEdit ? await updateDeal(deal.DealId, body) : await createDeal(body)
            successToast(isEdit ? 'Deal updated' : 'Deal created')
            invalidateCrm(...ALL_CRM_SCOPES)
            onSaved(saved)
        } catch (err) {
            setServerError(toApiError(err).message)
            if (toApiError(err).status === 409) invalidateCrm('deals', 'forecast')
        } finally {
            onBusyChange(false)
        }
    }

    return (
        <Form id={FORM_ID} noValidate onSubmit={handleSubmit(submit)} aria-busy={isSubmitting}>
            {serverError ? (
                <Message negative role="alert">
                    {serverError}
                </Message>
            ) : null}
            <FormTextFormField control={control} name="Name" label="Deal name" required placeholder="e.g. 6-site regional rollout" errors={errors.Name} defaultValue="" />
            <FieldError message={errors.Name?.message} />

            <FormDropdownField
                control={control}
                name="AccountId"
                label="Account"
                required
                placeholder="Choose an account"
                options={accountOptions}
                errors={errors.AccountId}
                defaultValue=""
                loading={accounts.loading}
                onChange={() => setValue('ContactId', '')}
            />
            <FieldError message={errors.AccountId?.message} />

            <FormDropdownField control={control} name="ContactId" label="Contact" options={contactOptions} defaultValue="" loading={contacts.loading} disabled={accountId === ''} />

            <Form.Group widths="equal">
                <FormTextFormField control={control} name="Amount" label="Amount (annual, $)" type="number" min="0" step="1" errors={errors.Amount} defaultValue="" />
                <FormTextFormField control={control} name="CloseDate" label="Expected close date" type="date" defaultValue="" />
            </Form.Group>
            <FieldError message={errors.Amount?.message} />

            <Form.Group widths="equal">
                <FormDropdownField control={control} name="Stage" label="Stage" options={stageOptions} defaultValue="New Lead" />
                <FormDropdownField control={control} name="Pipeline" label="Pipeline" options={pipelineOptions} defaultValue="New business" />
            </Form.Group>
            <FormDropdownField control={control} name="OwnerUserId" label="Owner" options={ownerOptions} defaultValue="" />
        </Form>
    )
}

/**
 * New / edit deal. Stage can never be Won here (that is the handoff dialog).
 * @param {{ open: boolean, deal?: import('utility/crm/types').CrmDeal|null, defaults?: {Stage?: string, Pipeline?: string, AccountId?: number},
 *   onClose: () => void, onSaved: (d: import('utility/crm/types').CrmDeal) => void }} props
 */
export const DealFormModal = ({ open, deal = null, defaults = {}, onClose, onSaved }) => {
    const [busy, setBusy] = useState(false)
    return (
        <CrmModal
            open={open}
            onClose={busy ? () => {} : onClose}
            title={deal ? 'Edit deal' : 'New deal'}
            width={600}
            footer={
                <>
                    <Button type="button" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="submit" form={FORM_ID} primary loading={busy} disabled={busy}>
                        {deal ? 'Save changes' : 'Create deal'}
                    </Button>
                </>
            }
        >
            <Body
                key={deal ? deal.DealId : `new-${defaults.Stage || ''}-${defaults.Pipeline || ''}`}
                deal={deal}
                defaults={defaults}
                onBusyChange={setBusy}
                onSaved={saved => {
                    onSaved(saved)
                    onClose()
                }}
            />
        </CrmModal>
    )
}
